import { Dialog, DialogBody, DialogContent, DialogHeader, Loader } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { RecommendationProductCode } from '@/api/rest/resources/types/recommendations';
import { CSV_REPORT_TYPES } from '@/api/rest/resources/types/reports';
import { DefaultWidgetNames, GraphTileType, WidgetType } from '@/api/rest/resources/types/widget';
import { withSuspenseBoundary } from '@/components';
import { GraphFact } from '@/components/Charts/types';
import { useGetActivePlotGroup } from '@/components/widgets/config/PlotGroupFilters';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useWidgetSlotConfigurationByNameType } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { CsvExportButton } from '@/pages/shared/reports/components/CsvExportButton';
import { isNullish } from '@/utils/booleans';

import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import { PlotListDesktop } from '../plots/components/PlotListDesktop';
import {
  useBiodiversityCells,
  useDefaultCustomCells,
  useHabitatIntactnessCells,
  useOrderedCustomCells,
} from '../plots/hooks/useGetCustomCells';
import { ProjectTypeAccessorKey } from '../projects/pages/project/pages/sustainableProduction/components/plot-list/constants';
import { LandPageLayout } from './components/LandPageLayout';

export const LandDeforestationList = withSuspenseBoundary(
  () => {
    const isSmallScreen = useScreenSize() === 'small';

    const defaultCells = useDefaultCustomCells();
    const orderedCustomCells = useOrderedCustomCells(defaultCells, [
      ProjectTypeAccessorKey.biodiversityZoneTrend_1y,
      ProjectTypeAccessorKey.biodiversityZone,
    ]);

    if (isSmallScreen) return null;

    const plotListFilters = {
      is_deforested: true,
    };

    return (
      <PlotListDesktop
        key='deforestation-plot-list'
        plotFilters={plotListFilters}
        customColumns={orderedCustomCells}
        pageSize={5}
        showSort={false}
        showActions={false}
      />
    );
  },
  <Loader className='min-h-[300px]' />,
);

const DeforestationDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: () => void }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl w-fit'>
        <DialogHeader className='typography-h3 text-text-primary w-full border-b-1 border-neutral-black-12'>
          {t('global.analysis.deforestation')}
        </DialogHeader>
        <DialogBody className='mb-8'>
          <LandDeforestationList />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export const LandBiodiversityPage = () => {
  const [deforestationDialogOpen, setDeforestationDialogOpen] = useState(false);
  const { t } = useTranslation();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const activeFilters = useGetActivePlotGroup();

  const missingDataLabel = t('shared.ncaDetail.details.habitatIntactness.labels.missingDataContext');

  const { [R1FactType.r1_biodiversity_zone_percent_historic_graph]: biodiversityZonePercentHistoricGraph } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(R1FactType.r1_biodiversity_zone_percent_historic_graph);
  const bpHistoricData = biodiversityZonePercentHistoricGraph?.value;

  const hasGraphData = (bpHistoricData?.length ?? 0) > 0;

  const showSummaryCard =
    !isLimitedAccessUser && !isNullish(activeFilters) && activeFilters.length === 0 && hasGraphData;

  const pofhHeaderProps = {
    title: t('shared.ncaDetail.details.biodiversity.labels.protectedOnFarmHabitat'),
    headerTooltip: t('shared.ncaDetail.details.biodiversity.explainers.protectedOnFarmHabitat.body'),
    showToggle: false,
    showZoom: true,
    showGraphType: true,
  };

  const ndviHeaderProps = {
    title: t('shared.ncaDetail.details.ndvi.labels.vegetationCondition'),
    headerTooltip: t('shared.ncaDetail.details.ndvi.labels.vegetationConditionTooltip'),
    showToggle: false,
    showZoom: true,
    showGraphType: true,
  };

  const habitatIntactnessHeaderProps = {
    title: t('shared.ncaDetail.details.habitatIntactness.labels.habitatIntactness'),
    headerTooltip: (
      <span>
        {t('shared.ncaDetail.details.habitatIntactness.labels.habitatIntactnessTooltip')}
        <br />
        <br />
        {missingDataLabel}
      </span>
    ),
    showToggle: false,
    showZoom: false,
    showGraphType: true,
  };

  const habitatIntactnessThreatsBreakdownHeaderProps = {
    title: t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.labels.threatsBreakdown'),
    headerTooltip: (
      <span>
        {t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.labels.threatsBreakdownTooltip')}
        <br />
        <br />
        {missingDataLabel}
      </span>
    ),
    showToggle: false,
    showZoom: false,
    showGraphType: true,
  };

  const habitatIntactnessThreatsTableHeaderProps = {
    title: t('shared.ncaDetail.details.habitatIntactness.threatsTable.labels.threatsTable'),
    headerTooltip: missingDataLabel,
  };

  const deforestationHeaderProps = {
    title: t('global.analysis.deforestation'),
    headerTooltip: t('shared.ncaDetail.details.deforestation.popoverBody'),
    showToggle: false,
  };

  const historicDeforestationHeaderProps = {
    title: t('shared.ncaDetail.details.deforestation.explainers.deforestation.title'),
    headerTooltip: t('shared.ncaDetail.details.deforestation.explainers.deforestation.body'),
    showToggle: false,
    showZoom: false,
    showGraphType: true,
  };

  const defaultWidgetSlotsConfiguration = {
    name: DefaultWidgetNames.default_template_asset_pofh,
    type: WidgetType.t_asset_page,
    widgets: [
      {
        name: 'pofh_asset_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: 'flex-col lg:flex-row items-stretch',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_current_pofh,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.r1_biodiversity_latest_analysis,
              headerText: 'global.widget.currentPofh',
              tooltipText: 'global.widget.currentPofhTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_pofh_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.biodiversity_buffer_zone_pct_previous_5y_pct_change,
              headerText: 'global.widget.historic5yAvgPofhUplift',
              tooltipText: 'global.widget.historic5yUpliftTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_projection_pofh_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.r1_biodiversity_buffer_zone_pct_previous_1y_uplift_pct_change,
              headerText: 'global.widget.projection1yPofhUplift',
              tooltipText: 'global.widget.projection1yUpliftTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_current_ndvi,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.vegetation_condition_ndvi,
              headerText: 'shared.ncaDetail.details.ndvi.labels.latestAnalysis',
              tooltipText: 'shared.ncaDetail.details.ndvi.labels.latestAnalysisTooltip',
            },
            visible: true,
          },
        ],
      },
      {
        name: 'biodiversity_summary',
        type: WidgetType.o_recommendation_summary,
        visible: showSummaryCard,
        relatedData: {
          productCode: RecommendationProductCode.EK_PROTECTED_ON_FARM_HABITAT,
        },
      },
      {
        name: 'asset_pofh_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: pofhHeaderProps,
          graphTileType: GraphTileType.protected_on_farm_habitat,
        },
        visible: true,
      },
      {
        name: 'deforestation_resilience_card',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          onClick: setDeforestationDialogOpen,
        },
        widgets: [
          {
            name: 'asset_deforestation_indicator_card',
            type: WidgetType.o_details_graph_card,
            relatedData: {
              headerProps: deforestationHeaderProps,
              graphTileType: GraphTileType.deforestation,
              styles: {
                root: 'hover:bg-neutral-black-4 hover:cursor-pointer',
              },
            },
            visible: true,
          },
        ],
      },
      {
        name: 'asset_ndvi_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: ndviHeaderProps,
          graphTileType: GraphTileType.ndvi,
        },
        visible: true,
      },
      {
        name: 'asset_historic_habitat_intactness_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: habitatIntactnessHeaderProps,
          graphTileType: GraphTileType.historic_habitat_intactness,
        },
        visible: true,
      },
      {
        name: 'asset_historic_habitat_intactness_threats_breakdown_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: habitatIntactnessThreatsBreakdownHeaderProps,
          graphTileType: GraphTileType.historic_habitat_intactness_threats_breakdown,
        },
        visible: true,
      },
      {
        name: 'asset_historic_habitat_intactness_threats_table',
        type: WidgetType.o_c_habitat_intactness_threats_table,
        relatedData: {
          headerProps: habitatIntactnessThreatsTableHeaderProps,
        },
        visible: true,
      },
      {
        name: 'asset_historic_deforestation_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: historicDeforestationHeaderProps,
          graphTileType: GraphTileType.historic_deforestation,
        },
        // TODO: MVP-8557 unhide deforestation cards for agriculture
        visible: false,
      },
    ],
  };

  const customWidgetSlotsConfiguration = useWidgetSlotConfigurationByNameType(
    DefaultWidgetNames.default_template_asset_pofh,
    WidgetType.t_page,
  );

  const widgetSlotsConfiguration = { ...defaultWidgetSlotsConfiguration, ...customWidgetSlotsConfiguration };

  const defaultCells = [...useDefaultCustomCells(), ...useBiodiversityCells(), ...useHabitatIntactnessCells()];
  const orderedCustomCells = useOrderedCustomCells(defaultCells, [
    ProjectTypeAccessorKey.habitatIntactnessTrend_5y,
    ProjectTypeAccessorKey.habitatIntactness,
    ProjectTypeAccessorKey.biodiversityZoneTrend_1y,
    ProjectTypeAccessorKey.biodiversityZone,
  ]);

  return (
    <LandPageLayout
      navBarProps={{
        children: <CsvExportButton reportType={CSV_REPORT_TYPES.BIODIVERSITY_CSV} className='w-fit font-normal' />,
      }}
      plotListProps={{
        customColumns: orderedCustomCells,
      }}
    >
      <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
      <DeforestationDialog
        open={deforestationDialogOpen}
        onOpenChange={() => setDeforestationDialogOpen(!deforestationDialogOpen)}
      />
    </LandPageLayout>
  );
};
